import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type ThemeColor = "cyan" | "mint" | "purple";
export type ThemeMode = "light" | "dark";

interface ThemeContextType {
  color: ThemeColor;
  mode: ThemeMode;
  setColor: (c: ThemeColor) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

function getStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function setStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {}
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [color, setColorState] = useState<ThemeColor>(() => {
    return (getStored("portfolio-color") as ThemeColor) || "cyan";
  });
  const [mode, setModeState] = useState<ThemeMode>(() => {
    return (getStored("portfolio-mode") as ThemeMode) || "light";
  });

  const setColor = (c: ThemeColor) => {
    setColorState(c);
    setStored("portfolio-color", c);
  };

  const toggleMode = () => {
    const next = mode === "light" ? "dark" : "light";
    setModeState(next);
    setStored("portfolio-mode", next);
  };

  useEffect(() => {
    const html = document.documentElement;
    html.classList.toggle("dark", mode === "dark");
    html.classList.remove("theme-cyan", "theme-mint", "theme-purple");
    html.classList.add(`theme-${color}`);
  }, [color, mode]);

  return (
    <ThemeContext.Provider value={{ color, mode, setColor, toggleMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
